<!--
	StreetViewButton: a split ButtonGroup segment that opens an external street-view tool for a feature's
	location. The caret opens an anchored popover listing every provider; picking one also makes it the default.
-->
<script lang="ts">
	import { STREET_VIEW_PROVIDERS, type StreetViewProvider } from '#lib/streetView.js';
	import { streetViewStore } from '#lib/stores/streetViewStore.svelte.js';
	import type { Position } from '#lib/featureGeometry.js';
	import IconArrowTopRightOnSquare from '#lib/assets/arrow-top-right-on-square.svg?raw';
	import IconChevronDown from '#lib/assets/chevron-down.svg?raw';

	interface Props {
		/** Feature location as `[lng, lat]`. */
		coords: NonNullable<Position>;
	}

	const { coords }: Props = $props();

	const id = $props.id();
	const anchorName = `--streetView-${id}`;

	let popoverEl: HTMLDivElement | undefined = $state();

	const selected = $derived(
		STREET_VIEW_PROVIDERS.find((provider) => provider.id === streetViewStore.provider) ??
			STREET_VIEW_PROVIDERS[0]
	);

	const urlFor = (provider: (typeof STREET_VIEW_PROVIDERS)[number]) =>
		provider.buildUrl(coords[0], coords[1]);

	function choose(providerId: StreetViewProvider) {
		streetViewStore.set(providerId);
		popoverEl?.hidePopover();
	}
</script>

<div class="streetViewSegment" style:anchor-name={anchorName}>
	<!-- external provider URL (built from feature coords), never a SvelteKit route -->
	<!-- eslint-disable-next-line svelte/no-navigation-without-resolve -->
	<a class="segment" href={urlFor(selected)} target="_blank" rel="noreferrer" title="Open {selected.label}">
		<!-- IconArrowTopRightOnSquare is a trusted static SVG asset (CLAUDE.md SVG rule) -->
		<!-- eslint-disable-next-line svelte/no-at-html-tags -->
		{@html IconArrowTopRightOnSquare}
		{selected.label}
	</a>
	<button
		type="button"
		class="segmentCaret"
		popovertarget={id}
		aria-label="Choose street-view provider">
		<!-- IconChevronDown is a trusted static SVG asset (CLAUDE.md SVG rule) -->
		<!-- eslint-disable-next-line svelte/no-at-html-tags -->
		{@html IconChevronDown}
	</button>

	<div
		{id}
		bind:this={popoverEl}
		class="providerMenu"
		popover="auto"
		style:position-anchor={anchorName}>
		<!-- A plain link list: menu roles would promise arrow-key navigation. -->
		<ul aria-label="Street-view providers">
			{#each STREET_VIEW_PROVIDERS as provider (provider.id)}
				<li>
					<!-- external provider URL (built from feature coords), never a SvelteKit route -->
					<!-- eslint-disable-next-line svelte/no-navigation-without-resolve -->
					<a href={urlFor(provider)} class="providerItem" class:selected={provider.id === selected.id} target="_blank" rel="noreferrer" onclick={() => choose(provider.id)}>
						<!-- IconArrowTopRightOnSquare is a trusted static SVG asset (CLAUDE.md SVG rule) -->
						<!-- eslint-disable-next-line svelte/no-at-html-tags -->
						{@html IconArrowTopRightOnSquare}
						<span>{provider.label}</span>
					</a>
				</li>
			{/each}
		</ul>
	</div>
</div>

<style>
	.streetViewSegment {
		display: flex;
		align-items: stretch;

		a.segment {
			color: inherit;
			text-decoration: none;
		}
	}

	[popover].providerMenu {
		position: fixed;
		position-area: block-end span-inline-end;
		position-try-fallbacks: flip-block, flip-inline;
		inset: auto;
		margin: var(--space-1) 0 0;
		box-shadow: var(--shadow-lg);
		border: 1px solid var(--border-color);
		border-radius: var(--radius);
		padding: var(--space-1);
		min-width: 13rem;

		ul {
			margin: 0;
			padding: 0;
			list-style: none;
		}

		.providerItem {
			display: flex;
			align-items: center;
			gap: var(--space-2);
			border-radius: var(--radius);
			padding: var(--space-2) var(--space-3);
			color: var(--text-color);
			font-size: var(--font-size-sm);
			text-decoration: none;
			white-space: nowrap;

			:global(svg) {
				flex-shrink: 0;
				width: var(--icon-sm);
				height: var(--icon-sm);
			}

			&:hover {
				background-color: var(--hover-surface);
			}

			&.selected {
				font-weight: var(--weight-semibold);
			}
		}
	}
</style>
